import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DICT } from '../i18n/dict';

export type Locale = 'pt-BR' | 'pt-PT' | 'en' | 'es';

export const LOCALES: { value: Locale; label: string; currency: string; tag: string }[] = [
  { value: 'pt-BR', label: 'Português (Brasil)', currency: 'BRL', tag: 'pt-BR' },
  { value: 'pt-PT', label: 'Português (Portugal)', currency: 'EUR', tag: 'pt-PT' },
  { value: 'en', label: 'English', currency: 'USD', tag: 'en-US' },
  { value: 'es', label: 'Español', currency: 'EUR', tag: 'es-ES' }
];

const STORAGE_KEY = 'nailsly.locale';

let active: Locale = 'pt-BR';

export function getLocale(): Locale {
  return active;
}

export function localeMeta(locale: Locale) {
  return LOCALES.find((item) => item.value === locale) ?? LOCALES[0];
}

export function localeTag(locale: Locale = active): string {
  return localeMeta(locale).tag;
}

export function currencyFor(locale: Locale = active): string {
  return localeMeta(locale).currency;
}

export function tr(key: string, vars?: Record<string, string | number>): string {
  if (active === 'pt-BR') {
    return applyVars(key, vars);
  }
  const text = DICT[active]?.[key] ?? key;
  return applyVars(text, vars);
}

function applyVars(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
}

export function money(cents: number | null | undefined, locale: Locale = active): string {
  if (cents === null || cents === undefined) return '—';
  const meta = localeMeta(locale);
  return new Intl.NumberFormat(meta.tag, {
    style: 'currency',
    currency: meta.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cents / 100);
}

export function readStoredLocale(): Locale {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'pt-PT' || value === 'en' || value === 'es' || value === 'pt-BR') return value;
  } catch {
    /* storage bloqueado: segue no padrão */
  }
  return 'pt-BR';
}

type I18nValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  money: (cents: number | null | undefined) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);
  active = locale;

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* modo privado sem storage: o idioma vale só nesta sessão */
    }
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale: setLocaleState,
      t: (key, vars) => tr(key, vars),
      money: (cents) => money(cents, locale)
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n precisa estar dentro de I18nProvider.');
  return value;
}
